import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DateFormatterService } from '@core/services/helpers/date-formatter.service';
import { DashboardService } from '@core/services/http/dashboard.service';
import { ImplicitIpcaCurveResponse } from '@interfaces/a-organizar/implicitIpcaCurve';
import { TranslateService } from '@ngx-translate/core';
import { VaultTranslatedBaseComponent } from '@shared/components/base/vault-translated-base.component';
import { VaultDateTimePipe } from '@shared/pipes/vault-date-time.pipe';
import { VaultFormatPercentPipe } from '@shared/pipes/vault-format-percent.pipe';

import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-di1-curve-chart',
  imports: [CommonModule, VaultDateTimePipe],
  providers: [VaultFormatPercentPipe],
  template: `
    <div class="di1-curve-chart">
      <div class="di1-curve-chart__header">
        <div class="di1-curve-chart__heading">
          <div class="card-info__item__title card-info__item__title--highlight">
            Curva de Juros × IPCA Implícito
          </div>
          <div class="di1-curve-chart__source">
            <span>Fonte: CMA · curvas de fechamento D-1 (B3)</span>
          </div>
        </div>
        <div class="di1-curve-chart__last-update" *ngIf="implicitIpcaCurveData?.lastUpdate">
          <span>Atualizado {{ implicitIpcaCurveData!.lastUpdate | vaultDateTime }}</span>
        </div>
      </div>
      <div class="di1-curve-chart__plot">
        <img
          class="di1-curve-chart__watermark"
          src="assets/images/logo-cognito.svg"
          alt=""
          aria-hidden="true" />
        <div #di1CurveChart class="di1-curve-chart__container"></div>
      </div>
    </div>
  `,
  styleUrls: ['./di1-curve-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Di1CurveChartComponent
  extends VaultTranslatedBaseComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() selectedCompanyId: string | null = null;
  @ViewChild('di1CurveChart', { static: true }) di1CurveChartElement!: ElementRef;

  private destroy$ = new Subject<void>();
  private chart: any | undefined;
  implicitIpcaCurveData: ImplicitIpcaCurveResponse | null = null;
  isLoading = false;
  private isBrowser: boolean;

  constructor(
    private dashboardService: DashboardService,
    private dateFormatter: DateFormatterService,
    private cdr: ChangeDetectorRef,
    private vaultFormatPercentPipe: VaultFormatPercentPipe,
    translateService: TranslateService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    super(translateService);
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.loadImplicitIpcaCurveData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCompanyId']) {
      this.loadImplicitIpcaCurveData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyChart();
  }

  /**
   * Uma unica chamada alimenta as duas series. O endpoint de IPCA implicito ja devolve,
   * por vertice, a taxa da curva de juros (`di1Rate`) interpolada na MESMA data — as duas
   * curvas vem do CMA source 87 e compartilham a grade, entao nao ha o que casar no front.
   */
  private loadImplicitIpcaCurveData(): void {
    this.isLoading = true;

    this.dashboardService
      .getImplicitIpcaCurve()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.implicitIpcaCurveData = response;
          this.updateChart();
          this.cdr.markForCheck();
        },
        error: error => {
          console.error('Erro ao carregar dados da curva implícita IPCA:', error);
          this.implicitIpcaCurveData = null;
          this.updateChart();
          this.cdr.markForCheck();
        },
        complete: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private async updateChart(): Promise<void> {
    if (
      !this.implicitIpcaCurveData?.points?.length ||
      !this.di1CurveChartElement ||
      !this.isBrowser
    ) {
      return;
    }

    try {
      const ApexCharts = (await import('apexcharts')).default;

      // Datas ISO (YYYY-MM-DD) ordenam corretamente como string — sem instanciar Date.
      const sortedPoints = [...this.implicitIpcaCurveData.points].sort((a, b) =>
        a.maturityDate.localeCompare(b.maturityDate)
      );

      // O gráfico é de COMPARAÇÃO, então para onde as duas curvas param de existir juntas. O IPCA vai
      // até ~14 anos e a curva de juros até ~9; além dessa ponta o backend só devolve o clamp do último
      // vértice do DI (marcado `ClampedToLastPoint`), e desenhar o IPCA sozinho ali deixava uma cauda
      // solta sem par. A ponta longa do IPCA continua disponível no Painel de Curvas e na API.
      const comparablePoints = sortedPoints.filter(
        point => point.calcTrace?.dapInterpolationMode !== 'ClampedToLastPoint'
      );
      // Backend antigo (sem `calcTrace`) não sabe marcar o clamp — nesse caso mostra tudo.
      const points = comparablePoints.length >= 2 ? comparablePoints : sortedPoints;

      // Formata como Mês/Ano (ex: "Mai/26", "Jun/26")
      const formatMonthYear = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      };

      // Um eixo só, com os vértices da curva: as duas séries saem do MESMO endpoint e portanto da
      // mesma grade do CMA source 87 (DC e DU idênticos, zero divergência). Antes o gráfico plotava
      // o IPCA (constant-maturity: 30D, 60D, 90D...) contra a curva DI1 LEGADA, que é por vencimento
      // de contrato (Jan/Abr/Jul/Out) — as duas não tinham UMA data em comum, e o casamento por
      // rótulo "Mês/Ano" descartava 17 dos 39 vértices de IPCA em silêncio.
      const toIsoDay = (dateString: string): string => dateString.slice(0, 10);

      const axisDays = points.map(point => toIsoDay(point.maturityDate));
      const categories = axisDays.map(day => formatMonthYear(day));

      const round4 = (value: number): number => Math.round(value * 10000) / 10000;

      // Sem null em nenhuma das duas: os vértices já vêm filtrados para a janela em que ambas existem.
      const ipcaData: number[] = points.map(point => round4(point.implicitIpcaRate));
      const diData: number[] = points.map(point => round4(point.di1Rate));

      const hasDiData = diData.some(value => Number.isFinite(value));
      const series: any[] = [];
      if (hasDiData) {
        series.push({ name: 'Taxa DI (%)', data: diData });
      }
      series.push({ name: 'IPCA Implícito (%)', data: ipcaData });

      const options: any = {
        series: series,
        chart: {
          type: 'line',
          height: 400,
          width: '100%',
          redrawOnWindowResize: true,
          animations: {
            enabled: true,
            speed: 800,
          },
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: true,
            offsetX: 0,
            offsetY: 6,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
            export: {
              csv: {
                filename: 'curva-di-ipca',
                columnDelimiter: ';',
                headerCategory: 'Vencimento',
                headerValue: 'Taxa (%)',
              },
              svg: {
                filename: 'curva-di-ipca',
              },
              png: {
                filename: 'curva-di-ipca',
              },
            },
          },
        },
        stroke: {
          width: 3,
          curve: 'smooth',
        },
        xaxis: {
          categories: categories,
          // O eixo unificado quase dobrou a contagem de categorias (as duas grades somadas). Sem limitar
          // os ticks os rótulos se sobrepõem; o dado de todos os vértices continua no gráfico e no
          // tooltip — só os rótulos são amostrados.
          tickAmount: Math.min(categories.length, 20),
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          labels: {
            style: {
              fontSize: '12px',
              fontFamily: "'Inter', sans-serif",
              colors: '#6C757D',
            },
            rotate: -45,
            rotateAlways: true,
          },
        },
        yaxis: [
          // A ordem dos eixos acompanha a das séries: DI primeiro (quando existe), IPCA sempre.
          ...(hasDiData
            ? [
                {
                  seriesName: 'Taxa DI (%)',
                  labels: {
                    style: {
                      fontSize: '12px',
                      fontFamily: "'Inter', sans-serif",
                      colors: '#6C757D',
                    },
                    formatter: (value: number) => {
                      return this.vaultFormatPercentPipe.transform(value, 2) || '';
                    },
                  },
                  title: {
                    text: 'Taxa DI (%)',
                    style: {
                      fontSize: '12px',
                      fontFamily: "'Inter', sans-serif",
                      color: '#6C757D',
                    },
                  },
                },
              ]
            : []),
          ...([
            {
              seriesName: 'IPCA Implícito (%)',
              opposite: true,
              labels: {
                style: {
                  fontSize: '12px',
                  fontFamily: "'Inter', sans-serif",
                  colors: '#E74C3C',
                },
                formatter: (value: number) => {
                  return this.vaultFormatPercentPipe.transform(value, 2) || '';
                },
              },
              title: {
                text: 'IPCA Implícito (%)',
                style: {
                  fontSize: '12px',
                  fontFamily: "'Inter', sans-serif",
                  color: '#E74C3C',
                },
              },
            },
          ] as any[]),
        ],
        grid: {
          borderColor: '#E5E5E5',
          strokeDashArray: 3,
          padding: {
            top: 24,
            right: 10,
            bottom: 0,
            left: 8,
          },
        },
        markers: {
          size: 4,
          // Com o eixo unificado, cada série tem null onde a outra é que publica vértice. Sem isto o
          // ApexCharts desenha marcador nessas posições vazias, sugerindo ponto que não existe.
          showNullDataPoints: false,
          colors: ['#3D4465', '#E74C3C'],
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: {
            size: 8,
            sizeOffset: 2,
          },
          fillOpacity: 1,
          strokeOpacity: 1,
        },
        colors: ['#3D4465', '#E74C3C'],
        tooltip: {
          enabled: true,
          shared: true,
          intersect: false,
          theme: 'light',
          style: {
            fontSize: '12px',
            fontFamily: "'Inter', sans-serif",
          },
          custom: ({ series, dataPointIndex }: any) => {
            const maturityDate = axisDays[dataPointIndex];

            const formattedDate = maturityDate
              ? new Date(maturityDate).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : '';

            const diValue = hasDiData ? series[0][dataPointIndex] : null;

            let content = `
              <div style="padding: 8px 12px; background: #fff; border: 1px solid #e5e5e5; border-radius: 6px; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);">
                <div style="font-size: 11px; color: #6c757d; margin-bottom: 4px;">
                  Vencimento: ${formattedDate}
                </div>
            `;

            // A curva de juros para em ~2035; nos vertices seguintes so o IPCA tem taxa.
            if (diValue !== null && diValue !== undefined) {
              const diFormatted = this.vaultFormatPercentPipe.transform(diValue, 4) || '';
              content += `
                <div style="font-weight: 600; margin-bottom: 2px; color: #3D4465;">
                  Taxa DI: ${diFormatted}
                </div>
              `;
            }

            const ipcaValue = series.length > 1 ? series[1][dataPointIndex] : null;
            if (ipcaValue !== null && ipcaValue !== undefined) {
              const ipcaFormatted = this.vaultFormatPercentPipe.transform(ipcaValue, 4) || '';
              content += `
                <div style="font-weight: 600; margin-bottom: 2px; color: #E74C3C;">
                  IPCA Implícito: ${ipcaFormatted}
                </div>
              `;
            }

            content += `</div>`;
            return content;
          },
        },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'right',
          offsetX: -50,
          offsetY: 6,
          fontSize: '12px',
          fontFamily: "'Inter', sans-serif",
          markers: {
            width: 12,
            height: 12,
            radius: 2,
          },
          itemMargin: {
            horizontal: 4,
          },
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                height: 300,
              },
              xaxis: {
                labels: {
                  rotate: -90,
                },
              },
            },
          },
        ],
      };

      this.destroyChart();
      this.chart = new ApexCharts(this.di1CurveChartElement.nativeElement, options);
      this.chart.render();
    } catch (error) {
      console.error('Erro ao carregar ApexCharts:', error);
    }
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private formatMaturityDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  formatLastUpdate(dateString: string): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      return (
        this.dateFormatter.formatDateInstant(date) +
        ' ' +
        date.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
        })
      );
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return dateString;
    }
  }
}
